import { useEffect, useRef } from 'react';

import { ArcElement, Chart as ChartJS, layouts, Legend, Tooltip, TooltipItem } from 'chart.js';
import i18n from 'i18next';
import { Doughnut } from 'react-chartjs-2';

import styles from './styles.module.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const Donut = ({ dataData, background, label, globalAmount, amounts }: Props) => {
  const chartRef = useRef<ChartJS<'doughnut'>>(null);

  useEffect(() => {
    document.fonts.ready.then(() => {
      chartRef.current?.update();
    });
  }, []);

  const data = {
    labels: label,
    layouts,
    datasets: [
      {
        data: dataData,
        backgroundColor: background,
        borderColor: background,
        cutout: '70%',
      },
    ],
  };

  const options = {
    rotation: -0.5 * Math.PI,

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (cDataData: TooltipItem<'doughnut'>) => {
            const value = cDataData.parsed || 0;
            const percent = i18n.t('format.percentFlexible', { value });
            // `amounts` est aligné sur `label` / `dataData` : une part sans montant connu
            // n'affiche que son pourcentage.
            const amount = amounts?.[cDataData.dataIndex];

            if (amount === undefined) return ` ${percent}`;

            return [` ${percent}`, ` ${i18n.t('format.currencyFlexible', { value: amount })}`];
          },
        },
      },
    },
  };

  const textCenter = {
    id: 'textCenter',
    beforeDatasetDraw(chart: ChartJS) {
      const firstPoint = chart.getDatasetMeta(0)?.data?.[0];
      if (!firstPoint) return;
      if (!globalAmount) return;
      const { ctx } = chart;
      const centerX = firstPoint.x;
      const centerY = firstPoint.y;
      ctx.save();
      try {
        ctx.font = "bold 20px 'Poppins', Arial, sans-serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Répartition globale', centerX, centerY - 25);
        ctx.font = "bold 30px 'Poppins', Arial, sans-serif";
        ctx.fillText(i18n.t('format.currencyFlexible', { value: globalAmount }), centerX, centerY + 25);
      } finally {
        ctx.restore();
      }
    },
  };

  return (
    <div className={styles.contentDonut}>
      <div className={styles.styleDonut}>
        <Doughnut ref={chartRef} data={data} options={options} plugins={[textCenter]} />
      </div>
    </div>
  );
};

export type Props = {
  label: string[];
  dataData: number[];
  background: string[];
  globalAmount?: number;
  amounts?: number[];
};

export default Donut;
